import {
  buildOntologyMatchingPlan,
  type OntologyMatchingAsset,
  type OntologyMatchingAssetKind,
  type OntologyMatchingDecision,
  type OntologyMatchingDecisionKind,
  type OntologyMatchingLlmPatch,
  type OntologyMatchingPlan,
} from "./application-ontology-matching";
import {
  buildDefaultFigmaTemplateBenchmarkCases,
  buildFigmaTemplateQualityReadiness,
  buildFigmaTemplateOntologyAssetSet,
  FIGMA_TEMPLATE_ONTOLOGY_BENCHMARK_SCHEMA_VERSION,
  type FigmaTemplateBenchmarkCase,
  type FigmaTemplateBenchmarkStatus,
  type FigmaTemplateBenchmarkTier,
  type FigmaTemplateNode,
  type FigmaTemplateOntologyAssetSet,
  type FigmaTemplateQualityReadiness,
  type FigmaTemplatePlatform,
} from "./figma-template-ontology-benchmark";

export const FIGMA_TEMPLATE_ONTOLOGY_MAPPING_SCHEMA_VERSION =
  "cogo.figma-template-ontology-mapping.v1";

export type FigmaTemplateOntologyMappingStatus = FigmaTemplateBenchmarkStatus;

export type FigmaTemplateOntologyMappingKind =
  | OntologyMatchingAssetKind
  | "design-token"
  | "action"
  | "data-input";

export interface FigmaTemplateOntologyNodeMapping {
  sourceKind: "figma-node";
  nodeId: string;
  nodeName: string;
  nodeType: string;
  sourceRef: string;
  ontologyAssetId: string;
  ontologyKind: OntologyMatchingAssetKind;
  decision: OntologyMatchingDecisionKind;
  authority: "tool";
  generationAllowed: boolean;
  candidateLifecycleStatus: OntologyMatchingDecision["candidateLifecycleStatus"];
  fallbackFrom?: OntologyMatchingAssetKind;
  score: number;
  evidenceRefs: string[];
  hardConstraintRefs: string[];
  reasons: string[];
}

export interface FigmaTemplateOntologyRequirementMapping {
  sourceKind: "selected-requirement";
  requirementId: string;
  ontologyKind: OntologyMatchingAssetKind;
  hardConstraintRef: string;
  decision?: OntologyMatchingDecisionKind;
  authority: "tool";
  generationAllowed: boolean;
  candidateLifecycleStatus:
    | OntologyMatchingDecision["candidateLifecycleStatus"]
    | "missing";
  fallbackFrom?: OntologyMatchingAssetKind;
  evidenceRefs: string[];
  reasons: string[];
}

export interface FigmaTemplateOntologyChunkMapping {
  sourceKind: "figma-chunk";
  chunkId: string;
  targetAssetIds: string[];
  evidenceRefs: string[];
  authority: "tool";
}

export interface FigmaTemplateOntologyTokenMapping {
  sourceKind: "design-token";
  token: string;
  tokenRef: string;
  designSystemId: string;
  authority: "tool";
  evidenceRefs: string[];
}

export interface FigmaTemplateOntologyActionMapping {
  sourceKind: "action";
  actionName: string;
  actionFlowRef: string;
  authority: "tool";
  evidenceRefs: string[];
  validationRequired: boolean;
  requiredValidation: readonly string[];
}

export interface FigmaTemplateOntologyDataInputMapping {
  sourceKind: "data-input";
  inputName: string;
  stateRef: string;
  authority: "tool";
  evidenceRefs: string[];
}

export interface FigmaTemplateOntologyMappingCaseResult {
  caseId: string;
  title: string;
  tier: FigmaTemplateBenchmarkTier;
  platform: FigmaTemplatePlatform;
  status: FigmaTemplateOntologyMappingStatus;
  reasons: string[];
  templateId: string;
  designSystemId: string;
  hardConstraints: string[];
  selectedAuthority: FigmaTemplateOntologyAssetSet["selected"];
  nodeMappings: FigmaTemplateOntologyNodeMapping[];
  requirementMappings: FigmaTemplateOntologyRequirementMapping[];
  chunkMappings: FigmaTemplateOntologyChunkMapping[];
  tokenMappings: FigmaTemplateOntologyTokenMapping[];
  actionMappings: FigmaTemplateOntologyActionMapping[];
  dataInputMappings: FigmaTemplateOntologyDataInputMapping[];
  qualityReadiness: FigmaTemplateQualityReadiness;
  matchingPlan: OntologyMatchingPlan;
}

export interface FigmaTemplateOntologyMappingReport {
  schemaVersion: typeof FIGMA_TEMPLATE_ONTOLOGY_MAPPING_SCHEMA_VERSION;
  benchmarkSchemaVersion: typeof FIGMA_TEMPLATE_ONTOLOGY_BENCHMARK_SCHEMA_VERSION;
  status: FigmaTemplateOntologyMappingStatus;
  caseCount: number;
  passedCaseCount: number;
  failedCaseCount: number;
  cases: FigmaTemplateOntologyMappingCaseResult[];
}

export interface BuildFigmaTemplateOntologyMappingProofOptions {
  llmPatchByCaseId?: Readonly<Record<string, OntologyMatchingLlmPatch>>;
}

type FlattenedFigmaNode = {
  node: FigmaTemplateNode;
  depth: number;
  parentId?: string;
};

type RequirementKind = Exclude<
  FigmaTemplateOntologyMappingKind,
  "design-token" | "action" | "data-input"
>;

export function buildFigmaTemplateOntologyMappingProof(
  benchmarkCases = buildDefaultFigmaTemplateBenchmarkCases(),
  options: BuildFigmaTemplateOntologyMappingProofOptions = {},
): FigmaTemplateOntologyMappingReport {
  const cases = benchmarkCases.map((benchmarkCase) => {
    const assetSet = buildFigmaTemplateOntologyAssetSet(benchmarkCase);
    const matchingPlan = buildOntologyMatchingPlan({
      intent: benchmarkCase.intent,
      chunks: assetSet.intentChunks,
      assets: assetSet.assets,
      selected: assetSet.selected,
      llmPatch: options.llmPatchByCaseId?.[benchmarkCase.id],
    });
    const flattened = flattenNodes(benchmarkCase.root);
    const decisionByRequirement = buildDecisionMap(matchingPlan.decisions);
    const nodeMappings = buildNodeMappings({
      benchmarkCase,
      assetSet,
      flattened,
      decisionByRequirement,
    });
    const requirementMappings = buildRequirementMappings({
      assetSet,
      decisionByRequirement,
    });
    const chunkMappings = buildChunkMappings(assetSet);
    const tokenMappings = buildTokenMappings(
      benchmarkCase,
      assetSet,
      flattened,
    );
    const actionMappings = buildActionMappings(
      benchmarkCase,
      matchingPlan,
      flattened,
    );
    const dataInputMappings = buildDataInputMappings(
      benchmarkCase,
      assetSet,
      flattened,
    );
    const qualityReadiness = buildFigmaTemplateQualityReadiness(benchmarkCase);
    const reasons = evaluateMappingCase({
      assetSet,
      matchingPlan,
      nodeMappings,
      requirementMappings,
      chunkMappings,
      tokenMappings,
      actionMappings,
      dataInputMappings,
      qualityReadiness,
    });
    return {
      caseId: benchmarkCase.id,
      title: benchmarkCase.title,
      tier: benchmarkCase.tier,
      platform: benchmarkCase.platform,
      status: reasons.length === 0 ? "pass" : "fail",
      reasons,
      templateId: assetSet.templateId,
      designSystemId: assetSet.designSystemId,
      hardConstraints: matchingPlan.hardConstraints,
      selectedAuthority: assetSet.selected,
      nodeMappings,
      requirementMappings,
      chunkMappings,
      tokenMappings,
      actionMappings,
      dataInputMappings,
      qualityReadiness,
      matchingPlan,
    } satisfies FigmaTemplateOntologyMappingCaseResult;
  });
  const failedCaseCount = cases.filter(
    (entry) => entry.status === "fail",
  ).length;
  return {
    schemaVersion: FIGMA_TEMPLATE_ONTOLOGY_MAPPING_SCHEMA_VERSION,
    benchmarkSchemaVersion: FIGMA_TEMPLATE_ONTOLOGY_BENCHMARK_SCHEMA_VERSION,
    status: failedCaseCount === 0 ? "pass" : "fail",
    caseCount: cases.length,
    passedCaseCount: cases.length - failedCaseCount,
    failedCaseCount,
    cases,
  };
}

function buildNodeMappings(input: {
  benchmarkCase: FigmaTemplateBenchmarkCase;
  assetSet: FigmaTemplateOntologyAssetSet;
  flattened: readonly FlattenedFigmaNode[];
  decisionByRequirement: ReadonlyMap<string, OntologyMatchingDecision>;
}): FigmaTemplateOntologyNodeMapping[] {
  const mappings: FigmaTemplateOntologyNodeMapping[] = [];
  const nodeRefToAssets = new Map<string, OntologyMatchingAsset[]>();
  for (const asset of input.assetSet.assets) {
    for (const ref of asset.ontologyRefs ?? []) {
      const assets = nodeRefToAssets.get(ref) ?? [];
      assets.push(asset);
      nodeRefToAssets.set(ref, assets);
    }
  }

  for (const entry of input.flattened) {
    const sourceRef = figmaNodeRef(input.benchmarkCase.id, entry.node.id);
    const nodeAssets = nodeRefToAssets.get(sourceRef) ?? [];
    const preferredAssets = preferNodeAssets(entry, nodeAssets);
    for (const asset of preferredAssets) {
      const decision = input.decisionByRequirement.get(
        decisionKey(asset.kind, asset.id),
      );
      if (!decision) continue;
      mappings.push(
        buildNodeMapping({
          node: entry.node,
          sourceRef,
          asset,
          decision,
          hardConstraintRefs: hardConstraintRefsForAsset(
            input.assetSet,
            asset.kind,
            asset.id,
          ),
        }),
      );
    }
  }

  return dedupeNodeMappings(mappings);
}

function buildRequirementMappings(input: {
  assetSet: FigmaTemplateOntologyAssetSet;
  decisionByRequirement: ReadonlyMap<string, OntologyMatchingDecision>;
}): FigmaTemplateOntologyRequirementMapping[] {
  return selectedRequirements(input.assetSet).map((requirement) => {
    const decision = input.decisionByRequirement.get(
      decisionKey(requirement.kind, requirement.id),
    );
    return {
      sourceKind: "selected-requirement",
      requirementId: requirement.id,
      ontologyKind: requirement.kind,
      hardConstraintRef: requirement.hardConstraintRef,
      decision: decision?.decision,
      authority: "tool",
      generationAllowed: decision?.generationAllowed ?? false,
      candidateLifecycleStatus: decision?.candidateLifecycleStatus ?? "missing",
      fallbackFrom: decision?.fallbackFrom,
      evidenceRefs: decision?.evidenceRefs ?? [
        `missing:${requirement.kind}:${requirement.id}`,
      ],
      reasons: decision?.reasons ?? [
        `Selected ${requirement.kind} ${requirement.id} had no matching decision.`,
      ],
    };
  });
}

function buildChunkMappings(
  assetSet: FigmaTemplateOntologyAssetSet,
): FigmaTemplateOntologyChunkMapping[] {
  return assetSet.intentChunks.map((chunk) => {
    const targetAssets = assetSet.assets
      .filter((asset) => asset.chunkHandles?.includes(chunk.id))
      .map((asset) => asset.id);
    return {
      sourceKind: "figma-chunk",
      chunkId: chunk.id,
      targetAssetIds: targetAssets,
      evidenceRefs:
        targetAssets.length > 0
          ? targetAssets.flatMap(
              (assetId) =>
                assetSet.assets.find((asset) => asset.id === assetId)
                  ?.ontologyRefs ?? [],
            )
          : [`missing:chunk:${chunk.id}`],
      authority: "tool",
    };
  });
}

function buildTokenMappings(
  benchmarkCase: FigmaTemplateBenchmarkCase,
  assetSet: FigmaTemplateOntologyAssetSet,
  flattened: readonly FlattenedFigmaNode[],
): FigmaTemplateOntologyTokenMapping[] {
  return assetSet.extractedTokens.map((token) => ({
    sourceKind: "design-token",
    token,
    tokenRef: `design-system:${assetSet.designSystemId}:token:${slugify(token)}`,
    designSystemId: assetSet.designSystemId,
    authority: "tool",
    evidenceRefs: flattened
      .filter(
        ({ node }) =>
          node.fills?.includes(token) || node.typography?.includes(token),
      )
      .map(({ node }) => figmaNodeRef(benchmarkCase.id, node.id)),
  }));
}

function buildActionMappings(
  benchmarkCase: FigmaTemplateBenchmarkCase,
  matchingPlan: OntologyMatchingPlan,
  flattened: readonly FlattenedFigmaNode[],
): FigmaTemplateOntologyActionMapping[] {
  return unique(flattened.flatMap(({ node }) => node.actions ?? [])).map(
    (actionName) => ({
      sourceKind: "action",
      actionName,
      actionFlowRef: `actionflow:${benchmarkCase.templateId}:${slugify(actionName)}`,
      authority: "tool",
      evidenceRefs: flattened
        .filter(({ node }) => node.actions?.includes(actionName))
        .map(({ node }) => figmaNodeRef(benchmarkCase.id, node.id)),
      validationRequired: matchingPlan.actionFlowImpact.required,
      requiredValidation: matchingPlan.actionFlowImpact.requiredValidation,
    }),
  );
}

function buildDataInputMappings(
  benchmarkCase: FigmaTemplateBenchmarkCase,
  assetSet: FigmaTemplateOntologyAssetSet,
  flattened: readonly FlattenedFigmaNode[],
): FigmaTemplateOntologyDataInputMapping[] {
  return unique(flattened.flatMap(({ node }) => node.dataInputs ?? [])).map(
    (inputName) => ({
      sourceKind: "data-input",
      inputName,
      stateRef: `template:${assetSet.templateId}:state:${slugify(inputName)}`,
      authority: "tool",
      evidenceRefs: flattened
        .filter(({ node }) => node.dataInputs?.includes(inputName))
        .map(({ node }) => figmaNodeRef(benchmarkCase.id, node.id)),
    }),
  );
}

function evaluateMappingCase(input: {
  assetSet: FigmaTemplateOntologyAssetSet;
  matchingPlan: OntologyMatchingPlan;
  nodeMappings: readonly FigmaTemplateOntologyNodeMapping[];
  requirementMappings: readonly FigmaTemplateOntologyRequirementMapping[];
  chunkMappings: readonly FigmaTemplateOntologyChunkMapping[];
  tokenMappings: readonly FigmaTemplateOntologyTokenMapping[];
  actionMappings: readonly FigmaTemplateOntologyActionMapping[];
  dataInputMappings: readonly FigmaTemplateOntologyDataInputMapping[];
  qualityReadiness: FigmaTemplateQualityReadiness;
}): string[] {
  const reasons: string[] = [];
  const hardConstraints = new Set(input.matchingPlan.hardConstraints);
  if (
    !hardConstraints.has(`design-system:${input.assetSet.designSystemId}:hard`)
  ) {
    reasons.push(
      "Selected design system is missing from mapping hard constraints.",
    );
  }
  if (!hardConstraints.has(`template:${input.assetSet.templateId}:hard`)) {
    reasons.push("Selected template is missing from mapping hard constraints.");
  }

  for (const mapping of input.nodeMappings) {
    if (mapping.authority !== "tool") {
      reasons.push(
        `Figma node ${mapping.nodeId} mapping used non-tool authority.`,
      );
    }
    if (mapping.candidateLifecycleStatus === "candidate") {
      reasons.push(
        `Figma node ${mapping.nodeId} maps to candidate-only ${mapping.ontologyKind} ${mapping.ontologyAssetId}.`,
      );
    }
    if (!mapping.generationAllowed) {
      reasons.push(
        `Figma node ${mapping.nodeId} maps to non-generatable ${mapping.ontologyKind} ${mapping.ontologyAssetId}.`,
      );
    }
    if (!mapping.evidenceRefs.includes(mapping.sourceRef)) {
      reasons.push(`Figma node ${mapping.nodeId} is missing source evidence.`);
    }
  }

  for (const requirement of input.requirementMappings) {
    if (requirement.candidateLifecycleStatus === "candidate") {
      reasons.push(
        `Required ${requirement.ontologyKind} ${requirement.requirementId} is candidate-only.`,
      );
    }
    if (requirement.candidateLifecycleStatus === "missing") {
      reasons.push(
        `Required ${requirement.ontologyKind} ${requirement.requirementId} has no mapping decision.`,
      );
    }
    if (!requirement.generationAllowed) {
      reasons.push(
        `Required ${requirement.ontologyKind} ${requirement.requirementId} is not allowed for generation.`,
      );
    }
    if (!hardConstraints.has(requirement.hardConstraintRef)) {
      reasons.push(
        `Required ${requirement.ontologyKind} ${requirement.requirementId} is missing hard constraint ${requirement.hardConstraintRef}.`,
      );
    }
  }

  for (const chunk of input.chunkMappings) {
    if (chunk.targetAssetIds.length === 0) {
      reasons.push(
        `Intent chunk ${chunk.chunkId} did not map to ontology assets.`,
      );
    }
  }

  if (input.tokenMappings.length === 0) {
    reasons.push(
      "No Figma design tokens mapped to the selected design system.",
    );
  }

  const actionInputCount = input.matchingPlan.actionFlowImpact.required
    ? input.actionMappings.length
    : 0;
  if (input.matchingPlan.actionFlowImpact.required && actionInputCount === 0) {
    reasons.push(
      "ActionFlow validation is required but no action mapping exists.",
    );
  }

  if (input.qualityReadiness.status !== "pass") {
    reasons.push(
      `Figma template quality readiness failed with score ${input.qualityReadiness.score}.`,
    );
    for (const metric of input.qualityReadiness.metrics) {
      if (metric.status === "fail") reasons.push(...metric.reasons);
    }
  }

  return unique(reasons);
}

function buildNodeMapping(input: {
  node: FigmaTemplateNode;
  sourceRef: string;
  asset: OntologyMatchingAsset;
  decision: OntologyMatchingDecision;
  hardConstraintRefs: string[];
}): FigmaTemplateOntologyNodeMapping {
  return {
    sourceKind: "figma-node",
    nodeId: input.node.id,
    nodeName: input.node.name,
    nodeType: input.node.type,
    sourceRef: input.sourceRef,
    ontologyAssetId: input.asset.id,
    ontologyKind: input.asset.kind,
    decision: input.decision.decision,
    authority: "tool",
    generationAllowed: input.decision.generationAllowed,
    candidateLifecycleStatus: input.decision.candidateLifecycleStatus,
    fallbackFrom: input.decision.fallbackFrom,
    score: input.decision.score,
    evidenceRefs: input.decision.evidenceRefs,
    hardConstraintRefs: input.hardConstraintRefs,
    reasons: input.decision.reasons,
  };
}

function preferNodeAssets(
  entry: FlattenedFigmaNode,
  assets: readonly OntologyMatchingAsset[],
): OntologyMatchingAsset[] {
  if (entry.depth === 0) {
    return assets.filter((asset) => asset.kind === "page");
  }
  const blockAssets = assets.filter((asset) => asset.kind === "ui-block");
  const componentAssets = assets.filter((asset) => asset.kind === "component");
  if (blockAssets.length > 0) {
    return [...blockAssets, ...componentAssets];
  }
  return componentAssets;
}

function selectedRequirements(assetSet: FigmaTemplateOntologyAssetSet): Array<{
  id: string;
  kind: RequirementKind;
  hardConstraintRef: string;
}> {
  return [
    ...(assetSet.selected.requiredPageIds ?? []).map((id) => ({
      id,
      kind: "page" as const,
      hardConstraintRef: `required-page:${id}`,
    })),
    ...(assetSet.selected.requiredUiBlockIds ?? []).map((id) => ({
      id,
      kind: "ui-block" as const,
      hardConstraintRef: `required-ui-block:${id}`,
    })),
    ...(assetSet.selected.requiredComponentIds ?? []).map((id) => ({
      id,
      kind: "component" as const,
      hardConstraintRef: `required-component:${id}`,
    })),
  ];
}

function hardConstraintRefsForAsset(
  assetSet: FigmaTemplateOntologyAssetSet,
  kind: OntologyMatchingAssetKind,
  assetId: string,
): string[] {
  return selectedRequirements(assetSet)
    .filter(
      (requirement) => requirement.kind === kind && requirement.id === assetId,
    )
    .map((requirement) => requirement.hardConstraintRef);
}

function buildDecisionMap(
  decisions: readonly OntologyMatchingDecision[],
): Map<string, OntologyMatchingDecision> {
  return new Map(
    decisions.map((decision) => [
      decisionKey(decision.kind, decision.requirementId),
      decision,
    ]),
  );
}

function decisionKey(
  kind: OntologyMatchingAssetKind,
  requirementId: string,
): string {
  return `${kind}:${requirementId}`;
}

function flattenNodes(
  node: FigmaTemplateNode,
  depth = 0,
  parentId?: string,
): FlattenedFigmaNode[] {
  return [
    { node, depth, parentId },
    ...(node.children ?? []).flatMap((child) =>
      flattenNodes(child, depth + 1, node.id),
    ),
  ];
}

function figmaNodeRef(caseId: string, nodeId: string): string {
  return `figma-template:${caseId}:node:${nodeId}`;
}

function dedupeNodeMappings(
  mappings: readonly FigmaTemplateOntologyNodeMapping[],
): FigmaTemplateOntologyNodeMapping[] {
  const byKey = new Map<string, FigmaTemplateOntologyNodeMapping>();
  for (const mapping of mappings) {
    byKey.set(
      `${mapping.nodeId}:${mapping.ontologyKind}:${mapping.ontologyAssetId}`,
      mapping,
    );
  }
  return [...byKey.values()];
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .trim();
}

function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}
